import { Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import usePageMeta from "@/hooks/usePageMeta";

const features = [
  {
    icon: "ri-newspaper-line",
    title: "及时资讯",
    desc: "追踪定档、开播与行业热点，让你第一时间掌握最关心的动漫动态。",
  },
  {
    icon: "ri-movie-2-line",
    title: "精选推荐",
    desc: "按类型、地区、年份分类整理热门与经典番剧，帮你快速找到下一部下饭好剧。",
  },
  {
    icon: "ri-user-star-line",
    title: "角色档案",
    desc: "深入了解人气角色的背景故事、配音阵容与相关作品，认识屏幕背后的灵魂。",
  },
];

const steps = [
  { step: "01", title: "浏览内容", desc: "通过首页或顶部导航进入资讯、番剧推荐与角色介绍专区。" },
  { step: "02", title: "筛选检索", desc: "在分类页使用筛选与排序，或直接使用站内搜索快速定位内容。" },
  { step: "03", title: "收藏分享", desc: "番剧与角色支持本地收藏，并可通过浏览器分享或复制链接。 " },
];

export default function About() {
  usePageMeta({
    title: "关于我们 - 香蕉动漫",
    description:
      "了解香蕉动漫的网站定位、运营团队与联系方式，以及本站的使用说明与版权声明。",
    keywords: "香蕉动漫,关于我们,动漫门户,运营团队",
  });
  return (
    <div className="w-full px-4 md:px-6 py-6 md:py-10">
      <Breadcrumb items={[{ label: "关于我们" }]} />

      <div className="mt-5">
        <h1 className="font-display font-black text-2xl md:text-4xl text-foreground-950">
          关于香蕉动漫
        </h1>
        <p className="mt-3 text-sm md:text-base leading-8 text-foreground-600 max-w-3xl">
          香蕉动漫是一个面向中国动漫爱好者的内容门户，聚焦「最新动漫资讯与精彩动画推荐」。
          我们希望用清晰、舒适、有活力的阅读体验，帮助每一位观众发现热门与经典的动画作品，
          无论是资深追番党还是刚刚入坑的新朋友，都能在这里找到属于自己的精彩世界。
        </p>
      </div>

      <section className="mt-10">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {features.map((f) => (
            <div key={f.title} className="rounded-lg border border-background-200 bg-background-50 p-5">
              <span className="w-12 h-12 flex items-center justify-center rounded-lg bg-primary-100 text-accent-700 text-2xl mb-4">
                <i className={f.icon}></i>
              </span>
              <h2 className="font-display font-bold text-lg text-foreground-950">{f.title}</h2>
              <p className="mt-2 text-sm leading-7 text-foreground-600">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-display font-black text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-3">
          <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
          网站使用说明
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {steps.map((s) => (
            <div key={s.step} className="rounded-lg bg-secondary-100 p-5">
              <span className="font-display font-black text-2xl text-accent-600">{s.step}</span>
              <h3 className="mt-2 font-display font-bold text-base text-foreground-950">{s.title}</h3>
              <p className="mt-1.5 text-sm leading-7 text-foreground-600">{s.desc}</p>
            </div>
          ))}
        </div>
        <div className="mt-4 rounded-lg border border-primary-200 bg-primary-50 px-4 py-3 text-sm text-foreground-700 leading-7">
          <strong className="font-bold">温馨提示：</strong>
          本站所有内容均为演示数据；「收藏」功能仅保存在当前设备与浏览器中，换设备或清除浏览器数据后将不再保留；
          「分享」功能调用系统分享接口或复制链接；站内搜索为前端模拟演示，真实搜索与内容接口待正式接入。
        </div>
      </section>

      <section className="mt-12">
        <h2 className="font-display font-black text-xl md:text-2xl text-foreground-950 mb-5 flex items-center gap-3">
          <span className="w-1.5 h-6 rounded-full bg-primary-500"></span>
          运营团队与联系
        </h2>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6">
            <h3 className="font-display font-bold text-lg text-foreground-950">香蕉动漫运营团队</h3>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              我们是一群热爱动漫的内容创作者与前端设计爱好者，致力于打造一个信息丰富、
              阅读舒适的动漫内容社区。团队日常的工作包括资讯采编、作品推荐与角色资料整理。
            </p>
            <ul className="mt-4 space-y-2.5 text-sm text-foreground-700">
              <li className="flex items-center gap-2">
                <i className="ri-mail-line w-4 h-4 flex items-center justify-center text-accent-600"></i>
                合作邮箱：hello@banana-anime.example
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-wechat-line w-4 h-4 flex items-center justify-center text-accent-600"></i>
                微信公众号：香蕉动漫（演示）
              </li>
              <li className="flex items-center gap-2">
                <i className="ri-map-pin-line w-4 h-4 flex items-center justify-center text-accent-600"></i>
                办公地址：中国 · 演示地址（虚拟）
              </li>
            </ul>
          </div>

          <div className="rounded-lg border border-background-200 bg-background-50 p-5 md:p-6 flex flex-col">
            <h3 className="font-display font-bold text-lg text-foreground-950">版权与声明</h3>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              本站展示的图片、文字与作品信息均为演示用途，不代表任何真实作品的官方立场。
              本站不提供真实的在线播放、下载或任何付费服务；如涉及版权问题，请联系运营团队处理。
            </p>
            <p className="mt-3 text-sm leading-7 text-foreground-600">
              © 2026 香蕉动漫 Banana Anime. 保留所有权利。
            </p>
            <div className="mt-auto pt-5 flex flex-wrap gap-3">
              <Link
                to="/category/news"
                className="inline-flex items-center gap-1.5 rounded-md bg-accent-500 px-4 py-2 text-sm font-bold text-white hover:bg-accent-600 transition-colors cursor-pointer whitespace-nowrap"
              >
                开始浏览资讯
                <i className="ri-arrow-right-line w-4 h-4 flex items-center justify-center"></i>
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-1.5 rounded-md border border-background-300 bg-background-50 px-4 py-2 text-sm font-medium text-foreground-800 hover:border-primary-300 transition-colors cursor-pointer whitespace-nowrap"
              >
                前往搜索
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}